import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Session, User } from '@supabase/supabase-js';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: any | null }>;
  signUp: (email: string, password: string) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any | null }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Configurer l'écouteur d'événements d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_IN') {
          toast({
            title: 'Connecté',
            description: 'Vous êtes maintenant connecté',
          });
        } else if (event === 'SIGNED_OUT') {
          toast({
            title: 'Déconnecté',
            description: 'Vous avez été déconnecté',
          });
        }
      }
    );

    // Vérifier s'il y a une session existante
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [toast]);

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      // Configurer les options de persistance en fonction de rememberMe
      const options = {
        auth: {
          persistSession: rememberMe
        }
      };
      
      // Utiliser les options dans signInWithPassword
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      // Si l'erreur est liée à la connexion réseau, on fournit un message plus clair
      if (error && (error.message === 'Failed to fetch' || error.message?.includes('network'))) {
        return { 
          error: {
            ...error,
            message: 'Problème de connexion réseau. Veuillez vérifier votre connexion internet.'
          }
        };
      }
      
      return { error };
    } catch (error: any) {
      // Amélioration de la gestion des erreurs de type réseau
      if (error.message === 'Failed to fetch' || error.message?.includes('network')) {
        return { 
          error: {
            message: 'Problème de connexion réseau. Veuillez vérifier votre connexion internet.'
          }
        };
      }
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      
      // Si l'erreur est liée à la connexion réseau, on fournit un message plus clair
      if (error && (error.message === 'Failed to fetch' || error.message?.includes('network'))) {
        return { 
          error: {
            ...error,
            message: 'Problème de connexion réseau. Veuillez vérifier votre connexion internet.'
          }
        };
      }
      
      return { error };
    } catch (error: any) {
      // Amélioration de la gestion des erreurs de type réseau
      if (error.message === 'Failed to fetch' || error.message?.includes('network')) {
        return { 
          error: {
            message: 'Problème de connexion réseau. Veuillez vérifier votre connexion internet.'
          }
        };
      }
      return { error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      // Si l'erreur est liée à la connexion réseau, on fournit un message plus clair
      if (error && (error.message === 'Failed to fetch' || error.message?.includes('network'))) {
        return { 
          error: {
            ...error,
            message: 'Problème de connexion réseau. Veuillez vérifier votre connexion internet.'
          }
        };
      }
      
      return { error };
    } catch (error: any) {
      // Amélioration de la gestion des erreurs de type réseau
      if (error.message === 'Failed to fetch' || error.message?.includes('network')) {
        return { 
          error: {
            message: 'Problème de connexion réseau. Veuillez vérifier votre connexion internet.'
          }
        };
      }
      return { error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
